import { useState, useEffect, useCallback } from 'react';
import * as api from '../api/api';

/**
 * Hook para gestionar la carga de agentes por supervisor y rango de fechas
 * @param {Object} dateRange - Rango de fechas con startDate y endDate
 * @param {string|null} selectedSupervisor - Supervisor seleccionado
 * @param {Function} t - Función de traducción
 * @returns {Object} { agents, agentsEnabled, isLoading, error, getAgentsPlaceholder }
 */
export function useAgents(dateRange, selectedSupervisor, t) {
  const [agents, setAgents] = useState([]);
  const [agentsEnabled, setAgentsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let abortController = new AbortController();

    const loadAgents = async () => {
      if (!dateRange?.startDate || !dateRange?.endDate) {
        setAgents([]);
        setAgentsEnabled(false);
        setIsLoading(false);
        setError(null);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        setAgentsEnabled(false);

        // Llamar API con UN supervisor
        const agentsRes = await api.getAgentsByDateRange(
          dateRange.startDate,
          dateRange.endDate,
          selectedSupervisor
        );

        if (abortController.signal.aborted) return;

        const uniqueAgents = [...new Set(agentsRes.items || [])].filter(a => a?.trim());
        const formattedAgents = uniqueAgents.sort().map(a => ({ value: a, label: a }));

        setAgents(formattedAgents);
        setAgentsEnabled(true);
      } catch (err) {
        console.error('Error loading agents:', err);
        setAgents([]);
        setAgentsEnabled(false);
        setError(err);
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    loadAgents();
    return () => abortController.abort();
  }, [dateRange, selectedSupervisor]);

  // Función mejorada para obtener placeholder con agentes seleccionados
  const getAgentsPlaceholderWithSelection = useCallback((selectedAgents) => {
    if (!selectedAgents || selectedAgents.length === 0) {
      return t("insights.all");
    }

    let placeholder = agents
      .filter(opt => selectedAgents.includes(opt.value))
      .map(opt => opt.label || opt.value)
      .join(", ");
    if (placeholder.length > 15) {
      // Agregar elipsis si es muy largo
      placeholder = `${placeholder.slice(0, 15)}...`
    }
    return placeholder;
  }, [agents, t]);

  return {
    agents,
    agentsEnabled,
    isLoading,
    error,
    getAgentsPlaceholder: getAgentsPlaceholderWithSelection
  };
}

